/**
 * Flow Manager - Handles questionnaire and screen navigation
 */

import type { FlowDependencies } from '../types/dependencies.types';
import { DependencyHelper } from './dependency-helper';
import { templateLoader } from './template-loader';
import { assetPreloader } from './asset-preloader';
import { initFAQAccordion } from './faq';
import { CongratulationsPopup } from '../paywall/congratulations';
import {
  getActiveFunnelExperience,
  type FunnelExperience,
} from './funnel-experience';

type ScreenName = string;
type ScreenHash = string;

export class FlowManager {
  private currentScreen: number = 0;
  private screens: ScreenName[] = [];
  private deps: Partial<FlowDependencies> = {};
  private experience: FunnelExperience | null = null;

  // User-friendly screen names for hash routing
  private screenHashes: Record<ScreenName, ScreenHash> = {
    landing: 'start',
    'landing-q8': 'welcome',
    'q8-feedback': 'feedback',
    email: 'email',
    q2: 'feeling',
    'q2-v2': 'feel',
    q3: 'goal',
    q4: 'superpower',
    q5: 'motivation',
    q6: 'genre',
    q7: 'venue',
    q8: 'voice',
    encouragement: 'progress',
    'voice-detection-info': 'detect-intro',
    'voice-detection': 'detect',
    songs: 'songs',
    technology: 'technology',
    community: 'community',
    commitment: 'practice',
    tools: 'tools',
    loading: 'creating',
    plan: 'your-plan',
    paywall: 'checkout',
    success: 'success',
    'science-backed': 'science-backed',
    happiness: 'happiness',
  };

  /**
   * Initialize flow - now accepts dependencies
   */
  init(deps: Partial<FlowDependencies> = {}): void {
    // Validate and fix dependencies
    this.deps = DependencyHelper.validateAndFixDependencies(
      deps,
      ['userRepository', 'statsManager', 'config'], // required dependencies
      'FlowManager'
    );

    // Set up screen flow based on A/B test variant
    const variant = this.deps.userRepository!.getABTestVariant();
    this.experience = variant === 'modern' ? getActiveFunnelExperience() : null;
    if (variant === 'modern' && !this.experience) {
      console.error('[FlowManager] Modern was assigned but its runtime did not load; using Purple');
    }
    if (this.experience) {
      this.screenHashes = { ...this.screenHashes, ...this.experience.screenHashes };
      document.documentElement.classList.add('funnel-v2-active');
      document.body.classList.add('funnel-v2-active');
    }
    this.screens = [
      'landing-q8',
      'science-backed',
      'q2-v2',
      'q3',
      'q4',
      'q5',
      'q6',
      'q7',
      'encouragement',
      'email',
      'songs',
      'technology',
      'community',
      'commitment',
      'tools',
      'loading',
      'plan',
      'paywall',
      'success',
    ];
    if (this.experience) {
      this.screens = [...this.experience.screens];
    } else if (variant === 'popunder-old-landing') {
      this.screens = [
        'landing',
        'landing-q8',
        'science-backed',
        'q2-v2',
        'q3',
        'q4',
        'q5',
        'q6',
        'q7',
        'encouragement',
        'email',
        'songs',
        'technology',
        'community',
        'commitment',
        'tools',
        'loading',
        'plan',
        'paywall',
        'success',
      ];
    } else if (variant === 'more-steps-a' || variant === 'more-steps-a2') {
      this.screens = [
        'landing-q8',
        'q8-feedback',
        'q2-v2',
        'q3',
        'q4',
        'q5',
        'q6',
        'q7',
        'encouragement',
        'email',
        'songs',
        'technology',
        'community',
        'commitment',
        'tools',
        'loading',
        'plan',
        'paywall',
        'success',
      ];
    } else if (variant === 'more-steps-b' || variant === 'more-steps-b2') {
      this.screens = [
        'landing-q8',
        'science-backed',
        'q2-v2',
        'q3',
        'happiness',
        'q4',
        'q5',
        'q8-feedback',
        'q6',
        'q7',
        'encouragement',
        'email',
        'songs',
        'technology',
        'community',
        'commitment',
        'tools',
        'loading',
        'plan',
        'paywall',
        'success',
      ];
    } else if (variant === 'bday-treatment-email-after') {
      this.screens = [
        'landing-q8',
        'science-backed',
        'q2-v2',
        'q3',
        'q4',
        'q5',
        'q6',
        'q7',
        'encouragement',
        'voice-detection-info',
        'voice-detection',
        'email',
        'songs',
        'technology',
        'community',
        'commitment',
        'tools',
        'loading',
        'plan',
        'paywall',
        'success',
      ];
    } else if (variant === 'bday-treatment-email-before') {
      this.screens = [
        'landing-q8',
        'science-backed',
        'q2-v2',
        'q3',
        'q4',
        'q5',
        'q6',
        'q7',
        'encouragement',
        'email',
        'voice-detection-info',
        'voice-detection',
        'songs',
        'technology',
        'community',
        'commitment',
        'tools',
        'loading',
        'plan',
        'paywall',
        'success',
      ];
    }

    // Update progress bars after screens are set
    this.updateAllProgressBars();

    // Keep preloading aligned with the logical flow indexes, but resolve experiment-specific
    // templates so Modern does not preload assets from the Purple paywall.
    const preloadScreens = this.screens.map((screenName) =>
      screenName === 'paywall' && this.experience
        ? this.experience.paywallTemplateName
        : screenName
    );
    assetPreloader.init(preloadScreens, this.experience?.rollingAssetPreload ?? false);

    this.experience?.init({
      userRepository: this.deps.userRepository!,
      statsManager: this.deps.statsManager,
      flow: this,
    });

    this.setupEventListeners();
    this.setupHashRouting();

    // NOTE: Don't navigate to initial screen here - wait for startNavigation() to be called
    // after all bootstrap phases complete. This ensures payment services exist before
    // navigating to paywall screen.
  }

  /**
   * Start navigation to initial screen
   * Called after all bootstrap phases complete to ensure payment services exist
   */
  startNavigation(): void {
    // Default to first screen
    const firstScreen = this.screens[0] || 'landing-q8';
    let screen: ScreenName = firstScreen;

    // Paddle's post-payment redirect lands here with ?paddle_success=1. It's a query
    // param rather than the #success hash because Paddle's checkout app rejects a '#'
    // in the successUrl (see PaddleCheckout.getSuccessUrl). Treat it as the success screen,
    // then strip the param so a reload or back-navigation doesn't re-force success
    // (any other params Paddle appended, e.g. _ptxn, are preserved).
    const params = new URLSearchParams(window.location.search);
    if (params.get('paddle_success') === '1') {
      params.delete('paddle_success');
      const query = params.toString();
      window.history.replaceState(null, '', `${window.location.pathname}${query ? `?${query}` : ''}#success`);
      this.goToScreen('success');
      return;
    }

    // Check for hash on initial load
    if (window.location.hash) {
      let hash = window.location.hash.substring(1);
      if (hash === 'subscription') {
        hash = 'checkout';
      }
      const hashScreen = this.getScreenFromHash(hash);
      if (hashScreen) {
        screen = hashScreen;
      }
    }

    if (this.requiresExperienceEmailBeforePaywall(screen)) {
      screen = this.experience!.emailScreenName;
      this.replaceScreenHash(screen);
    }
    this.goToScreen(screen);
  }

  private requiresExperienceEmailBeforePaywall(screenName: ScreenName): boolean {
    return Boolean(
      this.experience &&
      screenName === 'paywall' &&
      !this.validateEmail(this.deps.userRepository!.getEmail().trim())
    );
  }

  private replaceScreenHash(screenName: ScreenName): void {
    const hash = this.screenHashes[screenName];
    if (!hash) return;
    window.history.replaceState(
      window.history.state,
      '',
      `${window.location.pathname}${window.location.search}#${hash}`
    );
  }

  /**
   * Update dependencies after initialization (useful for circular dependencies)
   */
  updateDependencies(deps: Partial<FlowDependencies>): void {
    this.deps = { ...this.deps, ...deps };
  }

  /**
   * Setup hash routing
   */
  private setupHashRouting(): void {
    window.addEventListener('hashchange', () => {
      const hash = window.location.hash.substring(1);
      const screen = this.getScreenFromHash(hash);
      if (screen && screen !== this.getCurrentScreenName()) {
        this.goToScreen(screen);
      }
    });
  }

  /**
   * Get screen name from hash
   */
  private getScreenFromHash(hash: ScreenHash): ScreenName | null {
    for (const [screen, screenHash] of Object.entries(this.screenHashes)) {
      if (screenHash === hash && (!this.experience || this.screens.includes(screen))) {
        return screen;
      }
    }
    return null;
  }

  /**
   * Get current screen name
   */
  getCurrentScreenName(): ScreenName {
    return this.screens[this.currentScreen] || 'landing-q8';
  }

  /**
   * Navigate to next screen in the flow
   */
  goToNext(): void {
    if (this.currentScreen < this.screens.length - 1) {
      const nextScreen = this.screens[this.currentScreen + 1];
      if (nextScreen) {
        this.goToScreen(nextScreen);
      }
    }
  }

  /**
   * Navigate to previous screen
   */
  goBack(): void {
    if (this.currentScreen > 0) {
      const prevScreen = this.screens[this.currentScreen - 1];
      if (prevScreen) {
        this.goToScreen(prevScreen);
      }
    }
  }

  /**
   * Setup event listeners
   */
  private setupEventListeners(): void {
    // Start button
    document.querySelectorAll('[data-action="start"]').forEach((button) => {
      button.addEventListener('click', () => this.handleStart());
    });

    // Next buttons
    document.querySelectorAll('[data-next]').forEach((button) => {
      button.addEventListener('click', () => {
        const nextScreenAttr = button.getAttribute('data-next');
        if (nextScreenAttr === 'next') {
          this.goToNext();
        } else if (nextScreenAttr) {
          this.goToScreen(nextScreenAttr);
        }
      });
    });

    // Answer buttons
    document.querySelectorAll('[data-answer]').forEach((button) => {
      button.addEventListener('click', (e) => this.handleAnswer(e));
    });

    // Multi-select buttons
    document.querySelectorAll('[data-multi-answer]').forEach((button) => {
      button.addEventListener('click', (e) => this.handleMultiAnswer(e));
    });

    // Practice frequency buttons
    document.querySelectorAll('[data-practice]').forEach((button) => {
      button.addEventListener('click', (e) => this.handlePractice(e));
    });

    // Subscribe button
    document.getElementById('subscribe-btn')?.addEventListener('click', () => {
      this.handleSubscribe();
    });
  }

  /**
   * Handle start journey
   */
  private handleStart(): void {
    const emailInput = document.getElementById('email') as HTMLInputElement | null;
    if (emailInput && emailInput.value) {
      if (!this.validateEmail(emailInput.value)) {
        this.showError('Please enter a valid email address');
        return;
      }
      this.deps.userRepository!.setEmail(emailInput.value);
    }
    this.goToNext();
  }

  /**
   * Handle answer selection
   */
  private handleAnswer(e: Event): void {
    const button = e.currentTarget as HTMLElement;
    const answerData = button.getAttribute('data-answer');
    if (!answerData) return;

    const parts = answerData.split('-');
    const question = parts[0] || '';
    const answer = parts[1] || '';

    this.deps.userRepository!.setAnswer(question, answer);

    // Track answer in analytics
    if (this.deps.statsManager) {
      this.deps.statsManager.trackAnswer(question, answer, 'single');
    }

    const nextScreen = button.getAttribute('data-next');
    if (nextScreen === 'next') {
      setTimeout(() => this.goToNext(), 300);
    } else if (nextScreen) {
      setTimeout(() => this.goToScreen(nextScreen), 300);
    } else {
      setTimeout(() => this.goToNext(), 300);
    }
  }

  /**
   * Handle multi-select answers
   */
  private handleMultiAnswer(e: Event): void {
    const button = e.currentTarget as HTMLElement;
    const answerAttr = button.getAttribute('data-multi-answer');
    if (!answerAttr) return;

    const answer = answerAttr;
    const question = 'music';

    button.classList.toggle('selected');

    const currentAnswers = this.deps.userRepository!.get('multiSelectAnswers')?.[question] || [];

    const index = currentAnswers.indexOf(answer);
    if (index > -1) {
      currentAnswers.splice(index, 1);
    } else {
      if (currentAnswers.length < 2) {
        currentAnswers.push(answer);
      } else {
        button.classList.remove('selected');
        return;
      }
    }

    this.deps.userRepository!.setMultiSelectAnswer(question, currentAnswers);
    this.deps.userRepository!.setAnswer(question, currentAnswers);

    // Track genre selection if 2 genres selected
    if (currentAnswers.length === 2 && this.deps.statsManager) {
      this.deps.statsManager.trackGenreSelection(currentAnswers);
    }

    const continueBtn = document.querySelector('.continue-btn') as HTMLButtonElement | null;
    if (continueBtn) {
      continueBtn.disabled = currentAnswers.length === 0;
    }
  }

  /**
   * Handle practice frequency selection
   */
  private handlePractice(e: Event): void {
    const button = e.currentTarget as HTMLElement;
    const frequency = button.getAttribute('data-practice');
    if (!frequency) return;

    // Store frequency in answers
    this.deps.userRepository!.setAnswer('practice', frequency);

    // Track practice commitment
    if (this.deps.statsManager) {
      this.deps.statsManager.trackPracticeCommitment(frequency);
    }

    const nextScreen = button.getAttribute('data-next');
    if (nextScreen === 'next') {
      setTimeout(() => this.goToNext(), 300);
    } else if (nextScreen) {
      setTimeout(() => this.goToScreen(nextScreen), 300);
    } else {
      setTimeout(() => this.goToNext(), 300);
    }
  }

  /**
   * Handle subscribe
   */
  private handleSubscribe(): void {
    console.log('Subscribe clicked');

    // Ensure PaywallRenderer has a selected option
    const paywallRenderer = this.deps.paywallRenderer;
    if (paywallRenderer && !(paywallRenderer as any).selectedOption) {
      // Auto-select first option if none selected
      const firstOption = (paywallRenderer as any).currentView?.options[0];
      if (firstOption) {
        (paywallRenderer as any).selectOption(firstOption.price);
      }
    }

    // Open checkout via PaywallRenderer which will delegate to PaddleCheckout
    if (paywallRenderer && (paywallRenderer as any).selectedOption) {
      (paywallRenderer as any).openPaddleCheckout('paddle-checkout');
    } else {
      console.error('PaywallRenderer not available or no option selected');
    }
  }

  /**
   * Navigate to screen
   */
  goToScreen(screenName: ScreenName): void {
    const replaceGuardedHash = this.requiresExperienceEmailBeforePaywall(screenName);
    if (replaceGuardedHash) {
      screenName = this.experience!.emailScreenName;
    }
    if (this.experience && !this.screens.includes(screenName)) {
      console.warn(`[FlowManager] Ignoring screen outside the Modern funnel: ${screenName}`);
      return;
    }

    const currentScreenName = this.getCurrentScreenName();
    const currentScreenIndex = this.currentScreen;
    const hasActiveScreen = Boolean(document.querySelector('.screen.active'));
    if (hasActiveScreen && currentScreenName !== screenName) {
      this.experience?.onBeforeScreenChange?.(currentScreenName, screenName);
    }

    // Resume music if leaving voice-detection screen
    // const currentScreenName = this.screens[this.currentScreen];
    // if (currentScreenName === 'voice-detection' && screenName !== 'voice-detection') {
    //   const app = (window as any).app;
    //   if (app && app.resumeMusicAfterVoiceDetection) {
    //     app.resumeMusicAfterVoiceDetection();
    //   }
    // }

    const screens = document.querySelectorAll('.screen');
    const targetScreen = this.ensureScreenInDom(screenName);

    if (targetScreen) {
      screens.forEach((s) => {
        s.classList.remove('active');
        (s as HTMLElement).style.display = 'none';
        if (this.experience) {
          (s as HTMLElement).inert = true;
          s.setAttribute('aria-hidden', 'true');
        }
      });

      (targetScreen as HTMLElement).style.display = 'flex';
      if (this.experience) {
        (targetScreen as HTMLElement).inert = false;
        targetScreen.removeAttribute('aria-hidden');
      }
      targetScreen.classList.add('active');
      // Drop any pre-warm "fake hidden" state now that this screen is actually being shown.
      targetScreen.classList.remove('prewarming');

      if (this.experience) {
        const targetIndex = this.screens.indexOf(screenName);
        const direction = this.experience.getTransitionDirection(
          currentScreenIndex,
          targetIndex,
          hasActiveScreen
        );
        targetScreen.classList.remove('v2-enter-initial', 'v2-enter-forward', 'v2-enter-backward');
        void (targetScreen as HTMLElement).offsetWidth;
        targetScreen.classList.add(`v2-enter-${direction}`);
        targetScreen.dataset.v2Nav = direction;
      }

      // Scroll to top
      window.scrollTo(0, 0);
      document.body.scrollTop = 0;
      document.documentElement.scrollTop = 0;

      // Update current screen index
      this.currentScreen = this.screens.indexOf(screenName);

      // Trigger asset preloading for upcoming screens
      assetPreloader.onScreenOpen(this.currentScreen);

      // Update URL hash
      const hash = this.screenHashes[screenName];
      if (hash) {
        if (replaceGuardedHash) {
          this.replaceScreenHash(screenName);
        } else {
          window.location.hash = hash;
        }
      }

      // Track screen view in analytics
      if (this.deps.statsManager) {
        this.deps.statsManager.trackScreenView(screenName, hash || '');
      }

      this.experience?.onScreenShown(targetScreen as HTMLElement, screenName);

      // Special handling for certain screens
      if (screenName === 'loading') {
        setTimeout(() => {
          if (this.currentScreen === this.screens.indexOf('loading')) {
            this.goToNext();
          }
        }, 2000);
      }

      // Pre-warm the paywall's Apple Pay checkout ~2 screens before the paywall so the inline
      // Paddle iframe has time to finish its readiness/network warm-up before the user can tap —
      // this avoids the first-tap "popup" race. Screen order varies by A/B variant, so derive the
      // trigger position from this.screens instead of hardcoding a screen name. Idempotent; the
      // 'paywall' handler re-calls it as a no-op fallback (also covers direct #subscription access).
      const paywallIndex = this.screens.indexOf('paywall');
      if (paywallIndex >= 2 && screenName === this.screens[paywallIndex - 2]) {
        this.prewarmPaywall();
      }

      // Initialize gallery animation if songs screen
      if (screenName === 'songs' && this.deps.galleryManager) {
        (this.deps.galleryManager as any).init();
        (this.deps.galleryManager as any).optimizeAnimation();
      }

      // Restore multi-select button states for any screen with multi-select options
      this.restoreMultiSelectState(targetScreen as HTMLElement);

      // Voice detection screen handling
      if (screenName === 'voice-detection') {
        // Pause background music to avoid interference with voice detection
        const app = (window as any).app;
        if (app && app.pauseMusicForVoiceDetection) {
          app.pauseMusicForVoiceDetection();
        }

        const VoiceDetectionManager = (window as any).VoiceDetectionManager;
        if (VoiceDetectionManager && VoiceDetectionManager.isInitialized) {
          VoiceDetectionManager.onScreenEnter(targetScreen);
        }
      }

      // Handle paywall screen
      if (screenName === 'paywall') {
        // Track paywall view
        if (this.deps.statsManager) {
          this.deps.statsManager.trackPaywallView(true);
        }

        // Initialize carousel if not already done
        const SingersCarousel = (window as any).SingersCarousel;
        if (!this.experience && SingersCarousel && !SingersCarousel.carousels.has('paywall-carousel-container')) {
          SingersCarousel.renderCarousel('paywall-carousel-container');
        }

        // Initialize FAQ accordion on paywall screen
        this.initFAQ(targetScreen as HTMLElement);

        // Initialize the paywall (no-op if it was already pre-warmed a couple screens earlier)
        this.prewarmPaywall();

        // The 10-min offer countdown is decorative and should reflect time spent on the paywall,
        // not pre-warm time. (Re)start it now that the user has actually reached the paywall. If the
        // renderer is still initializing (cold direct #subscription access), init() starts the timer
        // itself once it sees the paywall is the active screen.
        const PaywallRendererInstance = (window as any).PaywallRenderer;
        if (!this.experience && PaywallRendererInstance?.isInitialized) {
          PaywallRendererInstance.startTimer();
        }
      }

      // Success screen handling
      if (screenName === 'success') {
        const popupOverlay = document.getElementById('checkout-popup-overlay');
        if (popupOverlay) {
          (popupOverlay as HTMLElement).style.display = 'none';
        }

        const emailElement = document.getElementById('success-email');
        const userEmail = this.deps.userRepository!.getEmail();
        if (emailElement && userEmail) {
          emailElement.textContent = userEmail;
        }

        // Show platform-appropriate download button(s)
        const ua = navigator.userAgent;
        const isIOS = /iPhone|iPad|iPod/.test(ua);
        const isAndroid = /Android/.test(ua);

        const iosBtn = document.getElementById('download-ios');
        const androidBtn = document.getElementById('download-android');
        const desktopBtns = document.getElementById('download-desktop');

        if (isIOS && iosBtn) {
          iosBtn.style.display = '';
        } else if (isAndroid && androidBtn) {
          androidBtn.style.display = '';
        } else if (desktopBtns) {
          desktopBtns.style.display = '';
        }

        // Purple retains its direct store-click tracking. Modern owns a pre-redirect
        // reminder and records the download only after the user confirms it.
        if (!this.experience) {
          const trackDownload = (store: string) => {
            if (this.deps.statsManager) {
              this.deps.statsManager.trackCTAClick(`download_${store}`, 'success');
            }
          };
          iosBtn?.querySelector('button')?.addEventListener('click', () => trackDownload('ios'));
          androidBtn?.querySelector('button')?.addEventListener('click', () => trackDownload('android'));
          desktopBtns?.querySelectorAll('a').forEach((a) => {
            const store = a.href.includes('apple.com') ? 'ios' : 'android';
            a.addEventListener('click', () => trackDownload(store));
          });
        }

        // Trigger fireworks animation
        const successScreen = document.getElementById('screen-success');
        const SuccessFireworks = (window as any).SuccessFireworks;
        if (successScreen && typeof SuccessFireworks !== 'undefined') {
          successScreen.classList.add('fireworks-active');
          setTimeout(() => {
            SuccessFireworks.start();
            setTimeout(() => {
              successScreen.classList.remove('fireworks-active');
            }, 8000);
          }, 500);
        }
      }

      if (screenName === 'landing') {
        const SingersCarousel = (window as any).SingersCarousel;
        if (SingersCarousel && !SingersCarousel.carousels.has('welcome-carousel-container')) {
          SingersCarousel.renderCarousel('welcome-carousel-container');
        }
      }

      // Update button visibility
      const app = (window as any).app;
      if (app && app.updateButtonVisibility) {
        app.updateButtonVisibility();
      }

      // Update and animate progress bar based on current flow order
      // Use requestAnimationFrame to ensure browser renders initial width before animating
      this.updateProgressBar(targetScreen, screenName);
      requestAnimationFrame(() => {
        this.animateProgressBar(targetScreen);
      });

      // Play background video on this screen (mobile browsers require explicit play)
      this.playBackgroundVideo(targetScreen);
    }
  }

  /**
   * Play background video on a screen (mobile browsers often block autoplay)
   */
  private playBackgroundVideo(screen: HTMLElement): void {
    const video = screen.querySelector('.background-video') as HTMLVideoElement | null;
    if (video) {
      // Try to play immediately
      video.play().catch(() => {
        // If autoplay blocked, wait for user interaction
        const playOnInteraction = (): void => {
          video.play().catch(() => {
            // Silent fail - video simply won't play
          });
          document.removeEventListener('touchstart', playOnInteraction);
          document.removeEventListener('click', playOnInteraction);
        };
        document.addEventListener('touchstart', playOnInteraction, { once: true });
        document.addEventListener('click', playOnInteraction, { once: true });
      });
    }
  }

  // Screens that should show progress bar (whitelist)
  private readonly screensWithProgressBar = [
    'landing-q8', 'science-backed', 'q8-feedback', 'q2', 'q2-v2', 'q3',
    'happiness', 'q4', 'q5', 'q6', 'q7', 'q8'
  ];

  /**
   * Update all progress bars based on screen flow
   * Note: This is called during init but most screens aren't in DOM yet.
   * The actual progress update happens in updateProgressBar() when navigating.
   */
  private updateAllProgressBars(): void {
    // Progress bars are now updated dynamically in updateProgressBar()
    // when each screen is navigated to, respecting the current flow order.
  }

  /**
   * Update progress bar for a specific screen based on current flow order
   */
  private updateProgressBar(screen: HTMLElement, screenName: string): void {
    const progressFill = screen.querySelector('.progress-fill') as HTMLElement | null;
    const progressText = screen.querySelector('.progress-text');

    if (!progressFill) return;

    // Get screens with progress bars in current flow order
    const progressScreens = this.screens.filter((s) => this.screensWithProgressBar.includes(s));
    const currentIndex = progressScreens.indexOf(screenName);

    if (currentIndex === -1) return;

    const totalSteps = progressScreens.length;
    const progressRange = 84; // 85% - 1%
    const progressIncrement = progressRange / (totalSteps - 1);

    const currentProgress = 1 + Math.max(0, currentIndex - 1) * progressIncrement;
    const targetProgress = 1 + currentIndex * progressIncrement;

    const currentPercent = Math.round(currentProgress * 100) / 100;
    const targetPercent = Math.round(targetProgress * 100) / 100;

    progressFill.style.width = `${currentPercent}%`;
    progressFill.setAttribute('data-target-width', `${targetPercent}%`);

    if (progressText) {
      progressText.textContent = `Step ${currentIndex + 1} of ${totalSteps}`;
    }
  }

  /**
   * Animate progress bar fill
   */
  private animateProgressBar(screen: HTMLElement): void {
    const progressFill = screen.querySelector('.progress-fill') as HTMLElement | null;
    if (progressFill) {
      const targetWidth = progressFill.getAttribute('data-target-width');
      if (targetWidth) {
        progressFill.style.width = targetWidth;
      }
    }
  }

  /**
   * Show screen by name
   */
  showScreen(screenName: ScreenName): void {
    this.goToScreen(screenName);
  }

  /**
   * Validate email
   */
  validateEmail(email: string): boolean {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  }

  /**
   * Show error message
   */
  private showError(message: string): void {
    const errorDiv = document.createElement('div');
    errorDiv.className = 'error-message';
    errorDiv.textContent = message;

    const emailInput = document.getElementById('email');
    if (emailInput && emailInput.parentNode) {
      const existing = (emailInput.parentNode as HTMLElement).querySelector('.error-message');
      if (existing) existing.remove();
      emailInput.parentNode.appendChild(errorDiv);

      setTimeout(() => errorDiv.remove(), 3000);
    }
  }

  /**
   * Restore multi-select button states from stored answers
   * Automatically detects question type from button onclick attributes
   */
  private restoreMultiSelectState(screen: HTMLElement): void {
    const multiSelectAnswers = this.deps.userRepository!.get('multiSelectAnswers');
    if (!multiSelectAnswers) return;

    // Find all option buttons with selectMultiOption and restore their selected state
    const buttons = screen.querySelectorAll('.option-button');
    buttons.forEach((button) => {
      const onclickAttr = button.getAttribute('onclick') || '';
      // Extract question and answer from onclick="app.selectMultiOption('music', 'pop', this)"
      const match = onclickAttr.match(/selectMultiOption\s*\(\s*['"]([^'"]+)['"]\s*,\s*['"]([^'"]+)['"]/);
      if (match) {
        const question = match[1];
        const answerValue = match[2];
        const selectedAnswers = question ? (multiSelectAnswers[question] || []) : [];
        if (answerValue && selectedAnswers.includes(answerValue)) {
          button.classList.add('selected');
        }
      }
    });

    // Update continue button state for music genres
    const continueBtn = screen.querySelector('#music-continue') as HTMLButtonElement | null;
    if (continueBtn && multiSelectAnswers.music) {
      const selectedCount = multiSelectAnswers.music.length;
      if (selectedCount === 0) {
        continueBtn.textContent = 'CONTINUE';
        continueBtn.disabled = true;
      } else if (selectedCount === 1) {
        continueBtn.textContent = 'SELECT ONE MORE';
        continueBtn.disabled = true;
      } else {
        continueBtn.textContent = 'CONTINUE';
        continueBtn.disabled = false;
      }
    }
  }

  /**
   * Return the DOM element for a screen, cloning it from its template (and initializing Alpine)
   * if it isn't in the DOM yet. Screens are created lazily on first visit.
   */
  private ensureScreenInDom(screenName: ScreenName): HTMLElement | null {
    let el = document.getElementById(`screen-${screenName}`);
    if (!el) {
      const templateName = screenName === 'paywall' && this.experience
        ? this.experience.paywallTemplateName
        : screenName;
      const fragment = templateLoader.cloneScreen(templateName);
      if (fragment) {
        document.body.appendChild(fragment);
        el = document.getElementById(`screen-${screenName}`);
        const Alpine = (window as any).Alpine;
        if (Alpine && el) {
          Alpine.initTree(el);
        }
      }
    }
    return el;
  }

  /**
   * Initialize the PaywallRenderer once prices are ready, which opens the inline Paddle checkout.
   * Called from the screen ~2 positions before the paywall to PRE-WARM the Apple Pay checkout
   * iframe so it's already loaded by the time the user reaches the paywall (avoids the first-tap
   * popup race), and again on the 'paywall' screen where the isInitialized guard makes it a no-op.
   *
   * Paddle won't initialize a checkout iframe inside a display:none element, so while pre-warming
   * (paywall screen not yet active) we render it "fake hidden" via the .prewarming class — full-size
   * but transparent, behind, and non-interactive — so the iframe actually loads & warms. The iframe
   * is never reparented (that would reload it); goToScreen() drops .prewarming when it goes active.
   */
  private prewarmPaywall(): void {
    const PaywallRenderer = (window as any).PaywallRenderer;
    const PaddleCheckout = (window as any).PaddleCheckout;
    if (
      typeof PaywallRenderer === 'undefined' ||
      PaywallRenderer.isInitialized ||
      !PaddleCheckout
    ) {
      return;
    }

    // Ensure the paywall screen exists and is rendered (not display:none) so Paddle can warm its
    // iframe. Only "fake hide" it when it isn't already the active (visible) screen.
    const paywallScreen = this.ensureScreenInDom('paywall');
    if (!paywallScreen) return;
    if (!paywallScreen.classList.contains('active')) {
      paywallScreen.classList.add('prewarming');
      if (this.experience) {
        paywallScreen.inert = true;
        paywallScreen.setAttribute('aria-hidden', 'true');
      }
    }

    const initializePaywall = (): void => {
      if (PaywallRenderer.isInitialized) return;
      if (
        PaddleCheckout.pricesFetchComplete &&
        Object.keys(PaddleCheckout.pricesData).length > 0
      ) {
        console.log('[Prewarm] Prices ready — initializing PaywallRenderer (opens inline checkout)');
        PaywallRenderer.init({
          userRepository: this.deps.userRepository,
          config: this.deps.config,
          paddleCheckout: PaddleCheckout,
          paywallConfig: (window as any).PAYWALL_CONFIG,
          paddlePricesMap: (window as any).PADDLE_PRICES_MAP,
          congratulationsPopup: CongratulationsPopup,
        });
      } else {
        console.log('[Prewarm] Waiting for all prices to load...');
        setTimeout(initializePaywall, 500);
      }
    };

    initializePaywall();
  }

  /**
   * Initialize FAQ accordion functionality within a screen
   */
  private initFAQ(screen: HTMLElement): void {
    initFAQAccordion(screen);
  }
}

// Create singleton instance
const flowManager = new FlowManager();

// Make globally available for backward compatibility
if (typeof window !== 'undefined') {
  (window as any).FlowManager = flowManager;
}

export default flowManager;
